"use client";

import React, { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  ArrowLeft,
  ArrowRight,
  KanbanSquare,
  Globe,
  ChevronDown,
  Building2,
  UtensilsCrossed,
  Dumbbell,
  Stethoscope,
  ShieldCheck,
  Headphones,
  CandlestickChart,
  Briefcase,
  Scale,
  Sparkles,
} from "lucide-react";

interface IndustryItem {
  id: string;
  name: string;
  shortName: string;
  websiteUrl: string;
  crmUrl: string;
  icon: any;
}

const INDUSTRIES: IndustryItem[] = [
  {
    id: "real-estate",
    name: "Real Estate & High-Yield Assets",
    shortName: "Real Estate",
    websiteUrl: "/demos/real-estate",
    crmUrl: "/demos/real-estate/crm",
    icon: Building2,
  },
  {
    id: "restaurant",
    name: "Restaurant & Fine Dining",
    shortName: "Restaurant",
    websiteUrl: "/demos/restaurant",
    crmUrl: "/demos/restaurant/crm",
    icon: UtensilsCrossed,
  },
  {
    id: "gym",
    name: "Gyms & Fitness Clubs",
    shortName: "Gym & Fitness",
    websiteUrl: "/demos/gym",
    crmUrl: "/demos/gym/crm",
    icon: Dumbbell,
  },
  {
    id: "dental",
    name: "Dental & Specialized Clinics",
    shortName: "Dental Clinic",
    websiteUrl: "/demos/dental",
    crmUrl: "/demos/dental/crm",
    icon: Stethoscope,
  },
  {
    id: "insurance",
    name: "Insurance & Wealth Management",
    shortName: "Insurance",
    websiteUrl: "/demos/insurance",
    crmUrl: "/demos/insurance/crm",
    icon: ShieldCheck,
  },
  {
    id: "telecalling",
    name: "Telecalling & BPO Agencies",
    shortName: "Telecalling",
    websiteUrl: "/demos/telecalling",
    crmUrl: "/demos/telecalling/crm",
    icon: Headphones,
  },
  {
    id: "stock-broking",
    name: "Stock Broking & Demat",
    shortName: "Stock Broking",
    websiteUrl: "/demos/stock-broking",
    crmUrl: "/demos/stock-broking/crm",
    icon: CandlestickChart,
  },
  {
    id: "hiring",
    name: "Staffing & Executive Search",
    shortName: "Staffing/HR",
    websiteUrl: "/demos/hiring",
    crmUrl: "/demos/hiring/crm",
    icon: Briefcase,
  },
  {
    id: "legal",
    name: "Law Firms & Legal Chambers",
    shortName: "Legal Chambers",
    websiteUrl: "/demos/legal",
    crmUrl: "/demos/legal/crm",
    icon: Scale,
  },
];

export default function DemoPortalTopBar() {
  const pathname = usePathname();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // If on root /demos, do not render sub bar
  if (!pathname || pathname === "/demos") {
    return null;
  }

  // Find active industry
  const currentIndustry = INDUSTRIES.find(
    (item) =>
      pathname === item.websiteUrl ||
      pathname === item.crmUrl ||
      pathname.startsWith(`${item.websiteUrl}/`)
  );

  if (!currentIndustry) {
    return null;
  }

  const isCrmView = pathname.includes("/crm");
  const CurrentIcon = currentIndustry.icon;

  const toggleUrl = isCrmView
    ? currentIndustry.websiteUrl
    : currentIndustry.crmUrl;
  const toggleLabel = isCrmView ? "View Client Website" : "Open Sales CRM";
  const ToggleIcon = isCrmView ? Globe : KanbanSquare;

  return (
    <div className="sticky top-[56px] sm:top-[64px] z-40 w-full bg-slate-950/95 backdrop-blur-md border-b border-amber-500/20 text-slate-200 text-xs shadow-lg">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-11 flex items-center justify-between gap-2">
        {/* Left: Back to Hub */}
        <div className="flex items-center gap-2 shrink-0">
          <Link
            href="/demos"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 hover:text-white transition-colors font-semibold group cursor-pointer"
            title="Return to All Industry Showcases"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-amber-400 group-hover:-translate-x-0.5 transition-transform" />
            <span className="hidden sm:inline">All Industry Demos</span>
            <span className="sm:hidden text-[11px]">All Demos</span>
          </Link>

          {/* Industry Switcher Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-900/80 hover:bg-slate-800 border border-slate-700/60 text-white font-bold transition-colors cursor-pointer"
            >
              <CurrentIcon className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span className="hidden md:inline">{currentIndustry.name}</span>
              <span className="md:hidden text-[11px] truncate max-w-[110px]">
                {currentIndustry.shortName}
              </span>
              <ChevronDown
                className={`w-3 h-3 text-slate-400 transition-transform ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {dropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setDropdownOpen(false)}
                />
                <div className="absolute left-0 top-full mt-1.5 w-64 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl py-2 z-50 animate-in fade-in max-h-80 overflow-y-auto">
                  <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Switch Industry Prototype
                  </div>
                  {INDUSTRIES.map((ind) => {
                    const Icon = ind.icon;
                    const isActive = ind.id === currentIndustry.id;
                    const destUrl = isCrmView ? ind.crmUrl : ind.websiteUrl;

                    return (
                      <Link
                        key={ind.id}
                        href={destUrl}
                        onClick={() => setDropdownOpen(false)}
                        className={`flex items-center gap-2.5 px-3 py-2 text-xs transition-colors ${
                          isActive
                            ? "bg-amber-500/10 text-amber-300 font-bold"
                            : "text-slate-300 hover:bg-slate-800 hover:text-white"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5 shrink-0 text-amber-400" />
                        <span className="truncate">{ind.name}</span>
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right: Dual View Toggle + Deploy CTA */}
        <div className="flex items-center gap-2 shrink-0">
          <Link
            href={toggleUrl}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg font-bold text-xs transition-all shadow-xs cursor-pointer ${
              isCrmView
                ? "bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200"
                : "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 shadow-amber-500/10"
            }`}
          >
            <ToggleIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{toggleLabel}</span>
            <span className="sm:hidden text-[11px]">
              {isCrmView ? "Website" : "Sales CRM"}
            </span>
          </Link>

          <Link
            href={`/register?industry=${currentIndustry.id}`}
            className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs transition-colors cursor-pointer shadow-sm"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Start 7-Day Trial</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
